import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Dialog, DialogContent, DialogTitle, Tab, Tabs } from '@mui/material'
import { useMutation, useQuery } from '@apollo/client'
import BackHeader from '../../components/BackHeader/BackHeader'
import Switch from '../../components/Switch/Switch'
import {
  CREATE_FODBOLDHOLD_INVITE,
  DELETE_FODBOLDHOLD_INVITE,
  SET_FODBOLDHOLD_INVITE_ACTIVE,
} from '../../graphql/mutations'
import { FODBOLDHOLD_INVITES } from '../../graphql/queries'
import { readImage, initialsFrom } from './logic'
import { useHold } from './store'
import Loading from '../../components/Loading/Loading'
import { DateTimeField, HoldScreen, TeamMark } from './bits'
import NoticeDialog from './NoticeDialog'
import Notification from '../../components/Notification/Notification'
import useNotification from '../../hooks/useNotification'

export default function SettingsView() {
  const navigate = useNavigate()
  const { team, current, isAdmin, dispatch, nameOf, loading } = useHold()
  const [notice, setNotice] = useState('')
  const { showNotification, notificationProps } = useNotification()
  const [seasonName, setSeasonName] = useState('')
  const [seasonOpen, setSeasonOpen] = useState(false)
  const [seasonBusy, setSeasonBusy] = useState(false)
  const [standingsOpen, setStandingsOpen] = useState(false)
  const [feeOpen, setFeeOpen] = useState(false)
  const [noticeOpen, setNoticeOpen] = useState(false)
  const [tab, setTab] = useState('look')
  const [memberQuery, setMemberQuery] = useState('')
  if (loading && !team) return <HoldScreen><Loading fill /></HoldScreen>
  if (!team || !current) return null
  if (!isAdmin) return <Navigate to={`/hold/${team.id}`} replace />

  const inviteLink = team.inviteCode ? `${window.location.origin}/hold/tilmeld/${team.inviteCode}` : ''

  return (
    <HoldScreen>
      <div className="hold-settings-top">
        <BackHeader
          eyebrow={team.name}
          title="Indstillinger"
          onBack={() => navigate(`/hold/${team.id}`)}
          actions={(
            <button
              type="button"
              className="hold-settings-button"
              aria-label="Send besked"
              onClick={() => setNoticeOpen(true)}
            >
              <CampaignOutlinedIcon />
            </button>
          )}
        />
        <div className="hold-settings-tabs">
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="fullWidth"
            textColor="inherit"
          >
            <Tab value="look" label="Udseende" />
            <Tab value="season" label="Generelt" />
            <Tab value="members" label="Medlemmer" />
          </Tabs>
        </div>
      </div>
      {notice ? <p className="hold-note">{notice}</p> : null}
      {noticeOpen ? (
        <NoticeDialog
          open
          team={team}
          season={current}
          nameOf={nameOf}
          onClose={() => setNoticeOpen(false)}
          onSent={(message) => showNotification(message, 'success')}
          onError={(message) => showNotification(message, 'error')}
        />
      ) : null}
      {tab === 'look' ? (
      <section className="hold-stack">
        <TeamImage
          label="Logo"
          image={team.logo}
          team={team}
          onChange={(logo) => dispatch({ type: 'SET_TEAM', teamId: team.id, fields: { logo } })}
        />
        <TeamImage
          label="Baggrund"
          image={team.background}
          wide
          onChange={(background) => dispatch({ type: 'SET_TEAM', teamId: team.id, fields: { background } })}
        />
      </section>
      ) : null}
      {tab === 'season' ? (
      <>
      <div className="hold-settings-actions">
        <button type="button" className="hold-action hold-action--ghost" onClick={() => setStandingsOpen(true)}>Stillingslink</button>
        <button type="button" className="hold-action" onClick={() => setFeeOpen(true)}>Kontingent</button>
        <button type="button" className="hold-action" onClick={() => setSeasonOpen(true)}>Ny sæson</button>
      </div>
      <Dialog
        open={feeOpen}
        onClose={() => setFeeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={settingsDialogPaper}
        BackdropProps={settingsDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Kontingent</DialogTitle>
        <DialogContent>
          {feeOpen ? (
            <TeamSettingsForm
              key={`${team.id}-${current.id}`}
              team={team}
              current={current}
              dispatch={dispatch}
              onNotice={setNotice}
              onDone={() => setFeeOpen(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={seasonOpen}
        onClose={seasonBusy ? undefined : () => setSeasonOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={settingsDialogPaper}
        BackdropProps={settingsDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Ny sæson</DialogTitle>
        <DialogContent>
          <form
            className="hold-form hold-create-form"
            onSubmit={async (event) => {
              event.preventDefault()
              if (!seasonName.trim() || seasonBusy) return
              setSeasonBusy(true)
              const result = await dispatch({ type: 'ADD_SEASON', teamId: team.id, name: seasonName })
              setSeasonBusy(false)
              if (result.ok) {
                setSeasonName('')
                setSeasonOpen(false)
              } else setNotice(result.error)
            }}
          >
            <label>Navn<input value={seasonName} onChange={(event) => setSeasonName(event.target.value)} placeholder="Forår 2027" /></label>
            <button type="submit" className="hold-action hold-action--save" disabled={!seasonName.trim() || seasonBusy}>
              {seasonBusy ? 'Starter…' : 'Start sæson'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={standingsOpen}
        onClose={() => setStandingsOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={settingsDialogPaper}
        BackdropProps={settingsDialogBackdrop}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Stillingslink</DialogTitle>
        <DialogContent>
          {standingsOpen ? (
            <StandingsForm
              key={current.standingsUrl || 'empty'}
              team={team}
              current={current}
              dispatch={dispatch}
              onNotice={setNotice}
              onDone={() => setStandingsOpen(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
      </>
      ) : null}

      {tab === 'members' ? (
      <>
      <section className="hold-stack">
        <h2>Medlemmer</h2>
        <p className="hold-note">Admin styrer holdet. Bøde-formand styrer bødekassen. Holdet skal blive ved med at have mindst én hold-admin.</p>
        <input
          className="hold-member-search"
          value={memberQuery}
          onChange={(event) => setMemberQuery(event.target.value)}
          placeholder="Søg medlem"
          aria-label="Søg medlem"
        />
        <ul className="hold-member-sheet">
          {team.members
            .filter((member) => nameOf(member.id).toLocaleLowerCase('da').includes(memberQuery.trim().toLocaleLowerCase('da')))
            .sort((a, b) => nameOf(a.id).localeCompare(nameOf(b.id), 'da'))
            .map((member) => {
            const onlyAdmin = member.isAdmin && team.members.filter((item) => item.isAdmin).length === 1
            const name = nameOf(member.id)
            return (
              <li key={member.id} className="hold-member-card">
                <div className="hold-member-person">
                  <span className="hold-fine-avatar" aria-hidden="true">{initialsFrom(name)}</span>
                  <span className="hold-member-name">{name}</span>
                </div>
                <div className="hold-member-switches">
                  <span className="hold-member-switch">
                    Admin
                    <Switch
                      checked={member.isAdmin}
                      disabled={onlyAdmin}
                      color="primary"
                      ariaLabel={member.isAdmin ? `Fjern hold-admin fra ${name}` : `Gør ${name} til hold-admin`}
                      onChange={(checked) => {
                        dispatch({
                          type: 'SET_HOLD_ADMIN',
                          teamId: team.id,
                          mateId: member.id,
                          isAdmin: checked,
                        }).then((result) => {
                          if (!result?.ok) setNotice(result?.error || 'Det kunne ikke ændres')
                        })
                      }}
                    />
                  </span>
                  <span className="hold-member-switch">
                    Bøde-formand
                    <Switch
                      checked={member.fineChair}
                      color="primary"
                      ariaLabel={member.fineChair ? `Fjern bøde-formand fra ${name}` : `Gør ${name} til bøde-formand`}
                      onChange={(checked) => {
                        dispatch({
                          type: 'SET_FINE_CHAIR',
                          teamId: team.id,
                          mateId: member.id,
                          fineChair: checked,
                        }).then((result) => {
                          if (!result?.ok) setNotice(result?.error || 'Det kunne ikke ændres')
                        })
                      }}
                    />
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
        {team.members.some((member) => nameOf(member.id).toLocaleLowerCase('da').includes(memberQuery.trim().toLocaleLowerCase('da'))) ? null : (
          <p className="hold-empty">Ingen medlemmer matcher.</p>
        )}
      </section>
      <section className="hold-stack">
        <h2>Eksisterende brugere</h2>
        <p className="hold-copy">Til folk der allerede har en bruger.</p>
        <div className="hold-link-sheet">
          <div className="hold-link-row">
            <div className="hold-link-copy">
              <span className="is-open">Fast link</span>
              <em>{inviteLink.replace(/^https?:\/\/[^/]+/, '')}</em>
            </div>
            <button
              type="button"
              className="hold-icon-button"
              aria-label="Kopier link"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(inviteLink)
                  showNotification('Kopieret', 'success')
                } catch {
                  showNotification('Kunne ikke kopiere linket.', 'error')
                }
              }}
            >
              <ContentCopyOutlinedIcon />
            </button>
          </div>
        </div>
      </section>

      <InviteLinks
        teamId={team.id}
        onNotice={setNotice}
        onCopied={() => showNotification('Kopieret', 'success')}
        onCopyError={() => showNotification('Kunne ikke kopiere linket.', 'error')}
      />
      </>
      ) : null}
      <Notification {...notificationProps} />
    </HoldScreen>
  )
}

function TeamSettingsForm({ team, current, dispatch, onNotice, onDone }) {
  const [mobilePay, setMobilePay] = useState(team.mobilePay || '')
  const [amount, setAmount] = useState(String(current.fee.amount ?? 0))
  const [due, setDue] = useState(current.fee.due || '')
  const [busy, setBusy] = useState(false)
  const changed = (
    mobilePay.trim() !== (team.mobilePay || '').trim()
    || (Number(amount) || 0) !== (current.fee.amount || 0)
    || due !== (current.fee.due || '')
  )

  return (
    <form
      className="hold-form hold-create-form"
      onSubmit={async (event) => {
        event.preventDefault()
        if (!changed || busy) return
        setBusy(true)
        const saved = await dispatch({
          type: 'SET_TEAM',
          teamId: team.id,
          fields: {
            mobilePay: mobilePay.trim(),
          },
        })
        if (!saved.ok) {
          setBusy(false)
          onNotice(saved.error)
          return
        }
        const fee = await dispatch({
          type: 'SET_FEE',
          teamId: team.id,
          seasonId: current.id,
          amount: Number(amount) || 0,
          due,
        })
        setBusy(false)
        if (fee.ok) onDone()
        else onNotice(fee.error)
      }}
    >
      <label>MobilePay-nummer<input value={mobilePay} onChange={(event) => setMobilePay(event.target.value)} /></label>
      <label>Kontingent for {current.name}<input value={amount} type="number" min="0" onChange={(event) => setAmount(event.target.value)} /></label>
      <label>Sidste betalingsdato<DateTimeField type="date" value={due} onChange={(event) => setDue(event.target.value)} /></label>
      <button type="submit" className="hold-action hold-action--save" disabled={!changed || busy}>
        {busy ? 'Gemmer…' : 'Gem'}
      </button>
    </form>
  )
}

function StandingsForm({ team, current, dispatch, onNotice, onDone }) {
  const [standings, setStandings] = useState(current.standingsUrl || '')
  const [busy, setBusy] = useState(false)
  const changed = standings.trim() !== (current.standingsUrl || '').trim()

  return (
    <form
      className="hold-form hold-create-form"
      onSubmit={async (event) => {
        event.preventDefault()
        if (!changed || busy) return
        setBusy(true)
        const result = await dispatch({
          type: 'SET_STANDINGS',
          teamId: team.id,
          seasonId: current.id,
          url: standings.trim(),
        })
        setBusy(false)
        if (result.ok) onDone()
        else onNotice(result.error)
      }}
    >
      <label>
        Link
        <input value={standings} inputMode="url" placeholder="https://" onChange={(event) => setStandings(event.target.value)} />
      </label>
      <button type="submit" className="hold-action hold-action--save" disabled={!changed || busy}>
        {busy ? 'Gemmer…' : 'Gem'}
      </button>
    </form>
  )
}

function TeamImage({ label, image, team, wide = false, onChange }) {
  return (
    <div className="hold-appearance">
      <p className="hold-meta">{label}</p>
      {wide ? (
        image ? <img className="hold-background-preview" src={image} alt="" /> : <p className="hold-empty">Ingen baggrund endnu.</p>
      ) : (
        <TeamMark team={{ ...team, logo: image }} className="hold-home-logo" />
      )}
      <div className="hold-row">
        <label className="hold-action hold-action--ghost hold-action--small">
          {image ? 'Skift' : 'Vælg billede'}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={async (event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (!file) return
              try {
                onChange(await readImage(file, wide ? 1400 : 512))
              } catch {
                onChange(image || null)
              }
            }}
          />
        </label>
        {image ? (
          <button type="button" className="hold-text-button" onClick={() => onChange(null)}>Fjern</button>
        ) : null}
      </div>
    </div>
  )
}

const settingsDialogPaper = {
  className: 'hold-create-paper',
  sx: {
    backgroundColor: '#221c24',
    color: '#f3eadf',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: '18px',
    boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
  },
}

const settingsDialogBackdrop = {
  sx: {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
}

function gqlError(error) {
  return error?.graphQLErrors?.[0]?.message || error?.message || 'Noget gik galt'
}

function inviteState(invite) {
  if (new Date(invite.expiresAt) <= new Date()) return 'Udløbet'
  if (invite.maxUses != null && invite.usedCount >= invite.maxUses) return 'Brugt op'
  if (!invite.active) return 'Slået fra'
  const count = invite.maxUses == null ? `${invite.usedCount}` : `${invite.usedCount} af ${invite.maxUses}`
  return `Åbent · ${count}`
}

function canEnable(invite) {
  return new Date(invite.expiresAt) > new Date() && (invite.maxUses == null || invite.usedCount < invite.maxUses)
}

function periodHours(amount, unit) {
  const end = new Date()
  const count = Number(amount)
  if (unit === 'hours') end.setHours(end.getHours() + count)
  if (unit === 'days') end.setDate(end.getDate() + count)
  if (unit === 'weeks') end.setDate(end.getDate() + count * 7)
  if (unit === 'months') end.setMonth(end.getMonth() + count)
  return Math.max(1, Math.round((end.getTime() - Date.now()) / 3600000))
}

function InviteLinks({ teamId, onNotice, onCopied, onCopyError }) {
  const [amount, setAmount] = useState('2')
  const [unit, setUnit] = useState('days')
  const [maxUses, setMaxUses] = useState('1')
  const [busy, setBusy] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const invites = useQuery(FODBOLDHOLD_INVITES, {
    variables: { teamId },
    fetchPolicy: 'network-only',
  })
  const [createInvite] = useMutation(CREATE_FODBOLDHOLD_INVITE)
  const [setActive] = useMutation(SET_FODBOLDHOLD_INVITE_ACTIVE)
  const [removeInvite] = useMutation(DELETE_FODBOLDHOLD_INVITE)
  const links = invites.data?.fodboldholdInvites || []

  const run = async (action) => {
    setBusy(true)
    try {
      await action()
      await invites.refetch()
      return true
    } catch (error) {
      onNotice(gqlError(error))
      return false
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="hold-stack">
      <h2>Nye spillere</h2>
      <p className="hold-copy">Linket opretter en bruger og giver adgang, indtil det stopper.</p>
      <button type="button" className="hold-action hold-action--save" onClick={() => setCreateOpen(true)}>
        Opret link
      </button>
      <Dialog
        open={createOpen}
        onClose={busy ? undefined : () => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          className: 'hold-create-paper',
          sx: {
            backgroundColor: '#221c24',
            color: '#f3eadf',
            border: '1px solid rgba(255,255,255,0.16)',
            borderRadius: '18px',
            boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        }}
      >
        <DialogTitle sx={{ color: '#f7f4ef', fontWeight: 800, paddingBottom: 0 }}>Opret link</DialogTitle>
        <DialogContent>
          <form
            className="hold-form hold-create-form"
            onSubmit={async (event) => {
              event.preventDefault()
              if (busy) return
              const uses = maxUses === '' ? null : Number(maxUses)
              const ok = await run(() => createInvite({ variables: { teamId, hours: periodHours(amount, unit), maxUses: uses } }))
              if (ok) setCreateOpen(false)
            }}
          >
            <label>Gyldig i
              <span className="hold-pickers">
                <select value={amount} onChange={(event) => setAmount(event.target.value)} aria-label="Antal">
                  {Array.from({ length: 60 }, (_, index) => index + 1).map((value) => (
                    <option key={value} value={String(value)}>{value}</option>
                  ))}
                </select>
                <select value={unit} onChange={(event) => setUnit(event.target.value)} aria-label="Enhed">
                  <option value="hours">Timer</option>
                  <option value="days">Dage</option>
                  <option value="weeks">Uger</option>
                  <option value="months">Måneder</option>
                </select>
              </span>
            </label>
            <label>Antal personer
              <select value={maxUses} onChange={(event) => setMaxUses(event.target.value)}>
                {Array.from({ length: 50 }, (_, index) => index + 1).map((value) => (
                  <option key={value} value={String(value)}>{value}</option>
                ))}
                <option value="">Uendelig</option>
              </select>
            </label>
            <button type="submit" className="hold-action hold-action--save" disabled={busy}>
              {busy ? 'Opretter…' : 'Opret'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
      {invites.error ? <p className="hold-note">{gqlError(invites.error)}</p> : null}
      {links.length === 0 ? <p className="hold-empty">Ingen links endnu.</p> : (
        <ul className="hold-link-sheet">
          {links.map((invite) => {
            const url = `${window.location.origin}/hold/inviter/${invite.token}`
            const state = inviteState(invite)
            const open = state.startsWith('Åbent')
            return (
              <li key={invite.id} className="hold-link-row">
                <div className="hold-link-copy">
                  <span className={open ? 'is-open' : ''}>{state}</span>
                  <em>Til {new Date(invite.expiresAt).toLocaleString('da-DK', { dateStyle: 'medium', timeStyle: 'short' })}</em>
                </div>
                <div className="hold-link-actions">
                  <button
                    type="button"
                    className="hold-icon-button"
                    aria-label="Kopier link"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(url)
                        onCopied()
                      } catch {
                        onCopyError()
                      }
                    }}
                  >
                    <ContentCopyOutlinedIcon />
                  </button>
                  {open || canEnable(invite) ? (
                    <button
                      type="button"
                      className="hold-action hold-action--ghost hold-action--small"
                      disabled={busy}
                      onClick={() => run(() => setActive({ variables: { id: invite.id, active: !invite.active } }))}
                    >
                      {invite.active ? 'Slå fra' : 'Slå til'}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="hold-icon-button hold-icon-button--delete"
                    aria-label="Slet link"
                    disabled={busy}
                    onClick={() => run(() => removeInvite({ variables: { id: invite.id } }))}
                  >
                    <DeleteOutlineIcon />
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
